import { Color } from 'three'
import { gsap } from 'gsap'

import { deepMerge, getTargetOffset, setElementHide } from 'js/utils/Utils'

import BasePlaneMesh from 'js/webgl/customMeshes/base/BasePlaneMesh'
import Common from 'js/_core/Common'

import VertexShader from 'js/webgl/_glsl/vert/bannerAMesh.vert'
import FragmentShader from 'js/webgl/_glsl/frag/bannerAMesh.frag'

const defaults = {
  instanceName: 'bannerAPlaneMesh',
  $names: {},
  effectInfo: {
    bgColor1: '#2b2b2b',
    bgColor2: '#b5b6c0',
  },
}

export default class BannerAPlaneMesh extends BasePlaneMesh {
  constructor(options = {}) {
    super({
      vs: VertexShader,
      fs: FragmentShader,
    })

    this.webgl = null
    this.uniforms = this.uniforms || {}
    this.isInView = false
    this.inViewAnimated = false
    this.width = 0
    this.height = 0

    this.originPos = {
      x: 0,
      y: 0,
    }

    this.meshOffset = {
      x: 0,
      y: 0,
    }
    this.meshProps = {}

    deepMerge(this, defaults, options)
  }

  init() {
    this._set$()
    super.init()
    this._setUniforms()
    this._setBaseSize()
    this.setResolutions()
    setElementHide(this.$target)
    this.setTexture()

    Common.webgl.stage.scene.add(this.mesh)

    this._addIoms()
  }

  destroy() {
    this._removeIoms()

    if (this.material.uniforms.uTexture && this.material.uniforms.uTexture.value) {
      this.material.uniforms.uTexture.value.dispose()
      this.material.uniforms.uTexture.value = null
    }

    if (this.mesh.material && this.mesh.material.uniforms.uTexture && this.mesh.material.uniforms.uTexture.value) {
      this.mesh.material.uniforms.uTexture.value.dispose()
      this.mesh.material.uniforms.uTexture.value = null
    }

    if (this.uniforms.uTexture && this.uniforms.uTexture.value) {
      this.uniforms.uTexture.value.dispose()
      this.uniforms.uTexture.value = null
    }

    super.destroy()
  }

  _set$() {
    if (this.$target) {
    }
  }

  _addIoms() {
    if (Common.iom) {
      Common.iom.add(this.$target, () => {
        if (!this.isInView) {
          this.isInView = true
          Common.iom.remove(this.$target)
        }
      })
    }
  }

  _removeIoms() {
    if (Common.iom) {
      Common.iom.remove(this.$target)
    }
  }

  _setUniforms() {
    deepMerge(this.uniforms, {
      uMeshOffset: {
        value: {
          x: 0,
          y: 0,
        },
      },
      uTexture: { value: null },
      uTextureResolution: {
        value: {
          x: 1,
          y: 1,
        },
      },
      uBgColor: { value: new Color(this.effectInfo.bgColor1).convertLinearToSRGB() },
      uBgColor2: { value: new Color(this.effectInfo.bgColor2).convertLinearToSRGB() },
      uOpacity: { value: 0 },
      uScaleInView: { value: 1 },
    })
  }

  setTexture() {
    const texturePath = this.$target.dataset['glImg']
    const textureList = Common.textureList['bannerA']
    const listLength = textureList.length

    for (let i = 0; i < listLength; i++) {
      const texture = textureList[i]
      if (texture.name === texturePath) {
        if (texturePath === texture.name) {
          let width = 1
          let height = 1

          if (texture.image) {
            width = texture.image.naturalWidth
            height = texture.image.naturalHeight
          }

          this.uniforms.uTextureResolution.value.x = width
          this.uniforms.uTextureResolution.value.y = height
          requestAnimationFrame(() => {
            this.uniforms.uTexture.value = texture
          })
        }
        break
      }
    }
  }

  setResolutions() {
    if (this.$target) {
      const rect = this.$target.getBoundingClientRect()
      this.width = rect.width
      this.height = rect.height

      this.uniforms.uMeshResolution.value.x = this.width
      this.uniforms.uMeshResolution.value.y = this.height

      this.originPos.x = -(Common.webgl.stage.stageSize.width * 0.5) + this.width * 0.5
      this.originPos.y = Common.webgl.stage.stageSize.height * 0.5 - this.height * 0.5

      const offset = getTargetOffset(this.$target)
      this.meshOffset.x = offset.left
      this.meshOffset.y = offset.top

      this.mesh.scale.z = 0.0001
    }
  }

  _setPosition() {
    this.uniforms.uMeshOffset.value.x = this.originPos.x + this.meshOffset.x
    this.uniforms.uMeshOffset.value.y = this.originPos.y - (this.meshOffset.y - Common.scroll.currentScroll)
  }

  _setBaseSize() {
    super._setBaseSize()
    // this.wh = window.innerHeight
  }

  inViewAnimation() {
    requestAnimationFrame(() => {
      gsap.to(this.uniforms.uScaleInView, {
        value: 0,
        duration: 1.5,
        ease: 'quart.out',
      })

      gsap.to(this.uniforms.uOpacity, {
        value: 1,
        duration: 0.5,
        ease: 'quad.inOut',
      })
    })
  }

  onResize() {
    this._setBaseSize()
    this.setResolutions()
    this._setPosition()
  }

  onRaf() {
    this._setPosition()
    if (Common.iomEnable && this.isInView && !this.inViewAnimated) {
      this.inViewAnimated = true
      this.inViewAnimation()
    }
  }
}